// RagMediaBlock — v1.16.0
// Rend les médias RAG (images / maps / liens) sous une bulle d'agent.
// Source: SSE event "media" ou champ `media` dans la réponse /api/agent.
import React, { useState } from "react";

function ImageCard({ url, caption }) {
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="rag-media-image"
      style={{
        display: "block",
        background: "var(--bg-secondary, #f8fafc)",
        border: "1px solid var(--border, #e2e8f0)",
        borderRadius: 12,
        overflow: "hidden",
        maxWidth: 320,
        textDecoration: "none",
        color: "inherit",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <img
        src={url}
        alt={caption || ""}
        loading="lazy"
        onError={(e) => { e.currentTarget.style.display = "none"; setHidden(true); }}
        style={{ width: "100%", height: "auto", display: "block" }}
      />
      {caption && (
        <div style={{ padding: "6px 10px", fontSize: 12, color: "var(--text-secondary, #475569)" }}>
          {caption}
        </div>
      )}
    </a>
  );
}

function MapCard({ url, embedUrl, caption }) {
  return (
    <div
      data-testid="rag-media-map"
      style={{
        background: "var(--bg-secondary, #f8fafc)",
        border: "1px solid var(--border, #e2e8f0)",
        borderRadius: 12,
        overflow: "hidden",
        maxWidth: 360,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <iframe
        src={embedUrl}
        title={caption || "map"}
        loading="lazy"
        width="100%"
        height="200"
        style={{ border: 0, display: "block" }}
        allowFullScreen={false}
        referrerPolicy="no-referrer-when-downgrade"
      />
      <div style={{ padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
        <span style={{ color: "var(--text-secondary, #475569)" }}>{caption}</span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--accent, #2563eb)", fontWeight: 500, textDecoration: "none" }}
          data-testid="rag-media-map-open"
        >
          Ouvrir →
        </a>
      </div>
    </div>
  );
}

function LinkCard({ url, caption }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="rag-media-link"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: "var(--bg-secondary, #f8fafc)",
        border: "1px solid var(--border, #e2e8f0)",
        borderRadius: 999,
        padding: "6px 12px",
        fontSize: 13,
        color: "var(--text-primary, #0f172a)",
        textDecoration: "none",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
      }}
    >
      <span aria-hidden="true">🌐</span>
      <span style={{ fontFamily: "monospace", fontSize: 12 }}>{caption || url}</span>
    </a>
  );
}

export default function RagMediaBlock({ media }) {
  if (!Array.isArray(media) || media.length === 0) return null;

  // Tri : maps + images en premier (visuels), puis liens.
  const visuals = media.filter(m => m.type === "image" || m.type === "map");
  const links = media.filter(m => m.type === "link");

  return (
    <div data-testid="rag-media-block" style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
      {visuals.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {visuals.map((m, i) => {
            if (m.type === "image") return <ImageCard key={`img-${i}`} url={m.url} caption={m.caption} />;
            if (m.type === "map")   return <MapCard key={`map-${i}`} url={m.url} embedUrl={m.embed_url} caption={m.caption} />;
            return null;
          })}
        </div>
      )}
      {links.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {links.map((m, i) => (
            <LinkCard key={`link-${i}`} url={m.url} caption={m.caption} />
          ))}
        </div>
      )}
    </div>
  );
}
